/**
 * SolflareBonusInfoTooltip Component
 *
 * Info icon with tooltip explaining the Solflare campaign bonus
 * Shows:
 * - What the bonus is (2x points)
 * - Campaign duration
 * - Progress (X/20,000)
 * - Time remaining
 *
 * Used on the Points page next to "Your Points" heading
 */

import { useState } from 'react';
import { Info } from 'lucide-react';

import { useSolflareCampaign } from '@/hooks/useSolflareCampaign';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} left`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} left`;
  }
  return 'Less than 1 hour left';
}

export function SolflareBonusInfoTooltip() {
  const { campaignData, isLoading } = useSolflareCampaign();
  const [isOpen, setIsOpen] = useState(false);

  // Only show if campaign is active
  if (isLoading || !campaignData?.isActive) {
    return null;
  }

  const isEligible = campaignData.isEligible;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;
  const progressPercent = (campaignData.userBonusEarned / campaignData.maxBonus) * 100;

  return (
    <div className="relative inline-flex">
      {/* Info Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200 hover:scale-110"
        style={{
          backgroundColor: 'rgba(255, 239, 70, 0.15)',
          border: '1px solid #FFEF46',
        }}
      >
        <Info className="w-4 h-4" style={{ color: '#FFEF46' }} />
      </button>

      {/* Tooltip Popover */}
      {isOpen && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: '2px solid #FFEF46',
            boxShadow: '0 0 20px rgba(255, 239, 70, 0.3)',
          }}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {/* Header with Icon */}
          <div className="flex items-center gap-2 mb-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                fill="#FFEF46"
              />
            </svg>
            <h4
              className="text-base font-bold"
              style={{ color: '#FFEF46', fontFamily: 'Outfit, sans-serif' }}
            >
              Solflare Campaign Bonus
            </h4>
          </div>

          {isEligible ? (
            <>
              {/* Active Bonus Message */}
              <p className="text-sm mb-3" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                You're earning <strong style={{ color: '#FFEF46' }}>2x points</strong> on chest openings with your Solflare wallet!
              </p>

              {/* Progress Section */}
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-2" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                  <span>Bonus Earned</span>
                  <span style={{ color: '#FFEF46', fontWeight: 600 }}>{progress} pts</span>
                </div>
                {/* Progress Bar */}
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(255, 239, 70, 0.2)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(progressPercent, 100)}%`,
                      backgroundColor: '#FFEF46',
                    }}
                  />
                </div>
                {campaignData.remainingBonus > 0 && (
                  <p className="text-xs mt-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    {campaignData.remainingBonus.toLocaleString()} pts remaining
                  </p>
                )}
              </div>

              {/* Duration Info */}
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(255, 239, 70, 0.1)',
                  border: '1px solid rgba(255, 239, 70, 0.3)',
                }}
              >
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: '#FFEF46', fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Promotion Message for Non-Solflare Users */}
              <p className="text-sm mb-3" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                Connect with <strong style={{ color: '#FFEF46' }}>Solflare wallet</strong> to earn 2x points on all chest openings!
              </p>

              {/* Call to Action */}
              <div
                className="p-3 rounded-lg mb-3 text-center"
                style={{
                  background: 'linear-gradient(92deg, #FFEF46 -4.02%, #FFF59D 24.3%, #FFEF46 50.55%, #FFF59D 72.17%, #FFEF46 103.05%)',
                }}
              >
                <p className="text-xs font-bold mb-1" style={{ color: '#000000', fontFamily: 'Outfit, sans-serif' }}>
                  Limited Time Offer
                </p>
                <p className="text-xs" style={{ color: '#000000', fontFamily: 'Outfit, sans-serif', opacity: 0.8 }}>
                  Earn up to 20,000 bonus points
                </p>
              </div>

              {/* Duration Info */}
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(255, 239, 70, 0.1)',
                  border: '1px solid rgba(255, 239, 70, 0.3)',
                }}
              >
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Offer ends in</span>
                  <span style={{ color: '#FFEF46', fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
